import React, { useState } from "react";
import {
  Box,
  IconButton,
  Typography,
  Slide,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";

interface YearNavigationProps {
  currentYear: number;
  onYearChange: (year: number) => void;
  minYear?: number;
  maxYear?: number;
}

const YearNavigation: React.FC<YearNavigationProps> = ({
  currentYear,
  onYearChange,
  minYear = 2020,
  maxYear = new Date().getFullYear(),
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationDirection, setAnimationDirection] = useState<'left' | 'right'>('right');

  const handlePreviousYear = () => {
    if (currentYear > minYear && !isAnimating) {
      setAnimationDirection('left');
      setIsAnimating(true);
      setTimeout(() => {
        onYearChange(currentYear - 1);
        setTimeout(() => setIsAnimating(false), 150);
      }, 150);
    }
  };

  const handleNextYear = () => {
    if (currentYear < maxYear && !isAnimating) {
      setAnimationDirection('right');
      setIsAnimating(true);
      setTimeout(() => {
        onYearChange(currentYear + 1);
        setTimeout(() => setIsAnimating(false), 150);
      }, 150);
    }
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      gap={2}
      sx={{
        py: 2,
        px: 3,
        borderRadius: 3,
        background: (theme) =>
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)"
            : "linear-gradient(135deg, #fff 0%, #f8f9fa 100%)",
        border: (theme) =>
          theme.palette.mode === "dark"
            ? "1px solid rgba(255,255,255,0.1)"
            : "1px solid rgba(0,0,0,0.05)",
        boxShadow: (theme) =>
          theme.palette.mode === "dark"
            ? "0 4px 12px rgba(0,0,0,0.2)"
            : "0 4px 12px rgba(0,0,0,0.05)",
      }}
    >
      <IconButton
        onClick={handlePreviousYear}
        disabled={currentYear <= minYear || isAnimating}
        size="small"
        sx={{
          width: 36,
          height: 36,
          borderRadius: 2,
          border: (theme) =>
            theme.palette.mode === "dark"
              ? "1px solid rgba(255,255,255,0.1)"
              : "1px solid rgba(0,0,0,0.1)",
          transition: "all 0.2s ease",
          "&:hover": {
            background: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.1)"
                : "rgba(0,0,0,0.05)",
            transform: "translateX(-1px)",
          },
          "&:disabled": {
            opacity: 0.5,
            transform: "none",
          },
        }}
      >
        <ChevronLeft fontSize="small" />
      </IconButton>

      <Box
        sx={{
          minWidth: 80,
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
          height: 32,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Slide
          direction={animationDirection === 'left' ? 'right' : 'left'}
          in={!isAnimating}
          timeout={{
            enter: 300,
            exit: 150,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: "1.25rem",
              background: (theme) =>
                theme.palette.mode === "dark"
                  ? "linear-gradient(135deg, rgba(34, 197, 94, 0.9) 0%, rgba(22, 163, 74, 0.8) 100%)"
                  : "linear-gradient(135deg, rgba(22, 163, 74, 0.9) 0%, rgba(34, 197, 94, 0.8) 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textAlign: "center",
              position: "absolute",
              width: "100%",
            }}
          >
            {currentYear}
          </Typography>
        </Slide>
      </Box>

      <IconButton
        onClick={handleNextYear}
        disabled={currentYear >= maxYear || isAnimating}
        size="small"
        sx={{
          width: 36,
          height: 36,
          borderRadius: 2,
          border: (theme) =>
            theme.palette.mode === "dark"
              ? "1px solid rgba(255,255,255,0.1)"
              : "1px solid rgba(0,0,0,0.1)",
          transition: "all 0.2s ease",
          "&:hover": {
            background: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.1)"
                : "rgba(0,0,0,0.05)",
            transform: "translateX(1px)",
          },
          "&:disabled": {
            opacity: 0.5,
            transform: "none",
          },
        }}
      >
        <ChevronRight fontSize="small" />
      </IconButton>
    </Box>
  );
};

export default YearNavigation;